import React, {ReactNode, useEffect} from 'react';
import {useRouteMatch, withRouter} from 'react-router-dom';
import {gql, useLazyQuery, useQuery} from '@apollo/client';
import {createStyles, Grid, Hidden, makeStyles, Typography} from '@material-ui/core';

import {Definition, LoaderDots, LogoWithText} from 'components/Widgets';

import {useDataServiceApi} from 'hooks/useDataServiceApi';
import useCurrentUser from 'hooks/useCurrentUser';
import {
  InfluencerPerformanceByPlatformResponse,
  InfluencerInfluenscoreData,
} from 'hooks/useDataServiceApi/types';

import {InfluencerInterests} from '../components';
import {InfluencerNavigationLinks, InfluencerProfile, InfluencerTags} from './components';
import InfoDump from './components/InfoDump';
import RewardsEngagementAndScore from './components/RewardsEngagementAndScore';
import GigEngagement from './components/GigEngagement/GigEngagement';
import {UserRoles} from 'consts/roles';
import useFeatureFlags from 'hooks/useFeatureFlags';

interface Props {
  children: ReactNode | ReactNode[];
}

const InfluencerWrapper: React.FC<Props> = ({children}) => {
  const classes = useStyles();
  const routeMatch = useRouteMatch();
  const params = routeMatch?.params as {influencerId: string; hideInfluencerNavigation: boolean};
  const currentUser = useCurrentUser();
  const {showInfluencerInfluenscore} = useFeatureFlags();

  const {influencerId, hideInfluencerNavigation} = params;

  const {
    error: dataApiError,
    loading: dataApiLoading,
    fetchInfluencerPerformanceByPlatform,
    fetchInfluenscoreInfluencerV1,
  } = useDataServiceApi();

  const [influencerPerformanceByPlatform, setInfluencerPerformanceByPlatform] = React.useState<
    InfluencerPerformanceByPlatformResponse | undefined
  >();

  const [influenscoreData, setInfluenscoreData] = React.useState<
    InfluencerInfluenscoreData | undefined
  >();

  const {
    loading: gqlLoading,
    data,
    error: gqlError,
  } = useQuery(InfluencerWrapperQuery, {
    variables: {id: influencerId},
  });
  const [loadCounts, countsData] = useLazyQuery(InfluencerWrapperCountQuery, {
    variables: {id: influencerId},
  });
  const loading = gqlLoading || dataApiLoading;
  const error = gqlError ?? dataApiError;

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser?.influxdataDataApiToken || !influencerId) return;

      const data = await fetchInfluencerPerformanceByPlatform({
        apiToken: currentUser?.influxdataDataApiToken,
        influencerId,
      });
      setInfluencerPerformanceByPlatform(data);

      // Fetch influenscore if feature flag is enabled and user is not 'advertiser'
      if (showInfluencerInfluenscore && (currentUser as User).roleName! !== UserRoles.Advertiser) {
        const influenscoreData = await fetchInfluenscoreInfluencerV1({
          apiToken: currentUser?.influxdataDataApiToken,
          influencerId,
        });
        if (influenscoreData && influenscoreData.length > 0) {
          setInfluenscoreData({
            cpf: influenscoreData?.[0].s_cpf ?? 0,
            cpe: influenscoreData?.[0].s_cpe ?? 0,
            cpr: influenscoreData?.[0].s_cpr ?? 0,
            cpm: influenscoreData?.[0].s_cpm ?? 0,
            overall: influenscoreData?.[0].influenscore ?? 0,
          });
        }
      } else {
        setInfluenscoreData(undefined);
      }
    };
    fetchData();
  }, [currentUser, influencerId, showInfluencerInfluenscore]);

  if (error) return <div>Unknown error encountered</div>;
  if (loading) return <LoaderDots />;

  const {influencer} = data;

  if (!influencer) {
    return <LogoWithText header="Influencer not found" />;
  }

  if (!hideInfluencerNavigation && !countsData.called) {
    loadCounts();
  }

  return (
    <Grid container alignItems="flex-start" spacing={2}>
      <Grid item xs={12}>
        <InfluencerProfile influencer={influencer} influenscoreData={influenscoreData} />
      </Grid>
      <Grid item container xs={12} md={8} direction="column" spacing={1}>
        <Grid item>
          <Typography variant="h5">Interests</Typography>
        </Grid>
        <Grid item>
          <InfluencerInterests influencer={influencer} />
        </Grid>
        {!hideInfluencerNavigation && (
          <Grid item xs={12} className={classes.navigation}>
            <InfluencerNavigationLinks
              influencer={influencer}
              mediaCount={countsData.data?.media?.count}
              gigCount={countsData.data?.gigs?.count}
              campaignCount={countsData.data?.campaigns?.count}
            />
          </Grid>
        )}
        <Grid item xs={12} className={classes.content}>
          {children}
        </Grid>
      </Grid>

      <Hidden mdDown>
        <Grid item md={1} />
      </Hidden>

      <Grid container item xs={12} md={3} spacing={4}>
        <Grid item>
          <RewardsEngagementAndScore influencer={influencer} />
        </Grid>
        {influencerPerformanceByPlatform?.length && (
          <Grid item>
            <GigEngagement data={influencerPerformanceByPlatform} />
          </Grid>
        )}
        <Grid item>
          <InfluencerTags influencer={influencer} />
        </Grid>
        <Grid item>
          <InfoDump influencer={influencer} />
        </Grid>
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles(() =>
  createStyles({
    interests: {
      marginTop: '20px',
    },
    navigation: {
      marginTop: '20px',
    },
    content: {
      marginTop: '20px',
    },
    tags: {
      marginTop: '20px',
    },
  })
);

const InfluencerWrapperQuery = gql`
  query InfluencerWrapper($id: UUIDString!) {
    influencer: profile(id: $id) {
      id
      ...InfluencerProfileFields
    }
  }
  ${InfluencerProfile.fragments.influencer}
`;

const InfluencerWrapperCountQuery = gql`
  query InfluencerWrapperCountQuery($id: UUIDString!) {
    campaigns: influencerCampaigns(id: $id) {
      count
    }
    gigs: gigsForInfluencer(id: $id) {
      count
    }
    media: instagramMediaByUsername(id: $id) {
      count
    }
  }
`;

export default withRouter(InfluencerWrapper);
